import React, { useEffect, useMemo, useRef, useState } from "react";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const DEFAULT_ZOOM = 14;

const BRAND = {
  red: "#D73C2E",
  black: "#252935",
  mix: "#B9343A",
  redSoftAlt: "#fff7f5",
  border: "rgba(185,52,58,0.14)",
  borderSoft: "rgba(185,52,58,0.12)",
  borderStrong: "rgba(185,52,58,0.20)",
  text: "#252935",
  muted: "#6b6f7d",
};

let googleMapsLoaderPromise = null;

function parseCoordinate(value) {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatCoordinate(value) {
  if (!Number.isFinite(Number(value))) return "";
  return Number(value).toFixed(6);
}

function loadGoogleMapsApi(apiKey) {
  if (!apiKey) return Promise.resolve(null);
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (googleMapsLoaderPromise) return googleMapsLoaderPromise;

  googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const callbackName = `__jkGoogleMapsPreviewInit_${Date.now()}`;
    const existingScript = document.querySelector('script[data-google-maps-loader="true"]');

    window[callbackName] = () => {
      delete window[callbackName];
      resolve(window.google.maps);
    };

    if (existingScript) {
      existingScript.addEventListener("error", () => reject(new Error("Failed to load Google Maps.")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.defer = true;
    script.dataset.googleMapsLoader = "true";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&loading=async&libraries=places&callback=${callbackName}&v=weekly`;
    script.onerror = () => {
      delete window[callbackName];
      googleMapsLoaderPromise = null;
      reject(new Error("Failed to load Google Maps."));
    };
    document.head.appendChild(script);
  });

  return googleMapsLoaderPromise;
}

export default function BirthLocationMapPreview({ latitude, longitude }) {
  const mapNodeRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);
  const [mapState, setMapState] = useState({
    loading: false,
    ready: false,
    error: "",
  });
  const [resolvedLocation, setResolvedLocation] = useState("");

  const latitudeNumber = useMemo(() => parseCoordinate(latitude), [latitude]);
  const longitudeNumber = useMemo(() => parseCoordinate(longitude), [longitude]);
  const hasCoordinates = latitudeNumber !== null && longitudeNumber !== null;
  const position = useMemo(
    () => (hasCoordinates ? { lat: latitudeNumber, lng: longitudeNumber } : null),
    [hasCoordinates, latitudeNumber, longitudeNumber]
  );
  const googleMapsLink = hasCoordinates
    ? `https://www.google.com/maps?q=${latitudeNumber},${longitudeNumber}`
    : "";

  useEffect(() => {
    let cancelled = false;

    if (!GOOGLE_MAPS_API_KEY || !hasCoordinates) {
      setMapState({ loading: false, ready: false, error: "" });
      return undefined;
    }

    setMapState((prev) => ({ ...prev, loading: true, error: "" }));

    loadGoogleMapsApi(GOOGLE_MAPS_API_KEY)
      .then(() => {
        if (cancelled || !mapNodeRef.current || mapRef.current || !position) return;

        mapRef.current = new window.google.maps.Map(mapNodeRef.current, {
          center: position,
          zoom: DEFAULT_ZOOM,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
        });

        markerRef.current = new window.google.maps.Marker({
          position,
          map: mapRef.current,
        });

        geocoderRef.current = new window.google.maps.Geocoder();
        setMapState({ loading: false, ready: true, error: "" });
      })
      .catch((error) => {
        if (cancelled) return;
        setMapState({
          loading: false,
          ready: false,
          error: error?.message || "Google Maps could not be loaded.",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [hasCoordinates, position]);

  useEffect(() => {
    if (!mapRef.current || !markerRef.current || !position) return;
    mapRef.current.setCenter(position);
    markerRef.current.setPosition(position);
  }, [position]);

  useEffect(() => {
    if (!geocoderRef.current || !position) return;

    geocoderRef.current.geocode({ location: position }, (results, status) => {
      if (status === "OK" && Array.isArray(results) && results[0]?.formatted_address) {
        setResolvedLocation(results[0].formatted_address);
        return;
      }
      setResolvedLocation("");
    });
  }, [position]);

  if (!hasCoordinates) return null;

  return (
    <div style={S.card}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Birth Location Coordinates</div>
          {resolvedLocation ? <div style={S.subtitle}>{resolvedLocation}</div> : null}
        </div>
        {googleMapsLink ? (
          <a href={googleMapsLink} target="_blank" rel="noreferrer" style={S.link}>
            Open in Google Maps
          </a>
        ) : null}
      </div>

      <div style={S.coordGrid}>
        <div style={S.coordItem}>
          <span style={S.coordLabel}>Latitude</span>
          <span style={S.coordValue}>{formatCoordinate(latitudeNumber)}</span>
        </div>
        <div style={S.coordItem}>
          <span style={S.coordLabel}>Longitude</span>
          <span style={S.coordValue}>{formatCoordinate(longitudeNumber)}</span>
        </div>
      </div>

      {GOOGLE_MAPS_API_KEY ? (
        <div style={S.mapShell}>
          <div ref={mapNodeRef} style={S.mapCanvas} />
          {mapState.loading ? <div style={S.overlay}>Loading Google Map...</div> : null}
          {mapState.error ? <div style={S.overlay}>{mapState.error}</div> : null}
        </div>
      ) : (
        <div style={S.notice}>
          Add `REACT_APP_GOOGLE_MAPS_API_KEY` to enable the map preview for saved coordinates.
        </div>
      )}
    </div>
  );
}

const S = {
  card: {
    gridColumn: "1 / -1",
    marginTop: 10,
    padding: 16,
    borderRadius: 22,
    border: `1px solid ${BRAND.border}`,
    background: "#fffaf8",
    display: "grid",
    gap: 14,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    flexWrap: "wrap",
  },
  title: {
    color: BRAND.text,
    fontSize: 14,
    fontWeight: 900,
  },
  subtitle: {
    marginTop: 4,
    color: BRAND.muted,
    fontSize: 12,
    fontWeight: 700,
  },
  link: {
    color: BRAND.red,
    fontSize: 12,
    fontWeight: 800,
    textDecoration: "none",
  },
  coordGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 12,
  },
  coordItem: {
    padding: "12px 14px",
    borderRadius: 16,
    background: "#fff",
    border: `1px solid ${BRAND.borderSoft}`,
    display: "grid",
    gap: 4,
  },
  coordLabel: {
    color: BRAND.muted,
    fontSize: 12,
    fontWeight: 800,
  },
  coordValue: {
    color: BRAND.text,
    fontSize: 16,
    fontWeight: 900,
  },
  mapShell: {
    position: "relative",
    borderRadius: 20,
    overflow: "hidden",
    border: `1px solid ${BRAND.borderSoft}`,
    minHeight: 280,
    background: "#f7f1ec",
  },
  mapCanvas: {
    width: "100%",
    height: 280,
  },
  overlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(255,255,255,0.78)",
    color: BRAND.muted,
    fontSize: 13,
    fontWeight: 800,
    padding: 16,
    textAlign: "center",
  },
  notice: {
    borderRadius: 16,
    border: `1px dashed ${BRAND.borderStrong}`,
    background: BRAND.redSoftAlt,
    color: BRAND.muted,
    fontSize: 12,
    fontWeight: 700,
    padding: "12px 14px",
  },
};
